import { createApp, h } from 'vue';
import AlertNotification from '@/components/ui/AlertNotification.vue';

const DEFAULT_DURATION = 5000; // 5 seconds
let notificationContainer = null;

/**
 * Create a notification container if it doesn't exist yet
 */
function createContainer() {
  if (notificationContainer) return;
  
  notificationContainer = document.createElement('div');
  notificationContainer.className = 'notification-container';
  document.body.appendChild(notificationContainer);
  
  // Add styles
  const style = document.createElement('style');
  style.textContent = `
    .notification-container {
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      width: 360px;
      max-width: calc(100vw - 40px);
      display: flex;
      flex-direction: column;
      gap: 10px;
      pointer-events: none;
    }
    .notification-container > div {
      pointer-events: auto;
    }
  `;
  document.head.appendChild(style);
}

/**
 * Show a notification with the provided options
 *
 * @param {Object} options - The notification options
 * @param {string} options.type - The type of notification: success, info, warning, error
 * @param {string} [options.title] - Optional title for the notification
 * @param {string} [options.message] - Message content (if not using a custom component)
 * @param {boolean} [options.dismissible=true] - Whether the notification can be dismissed
 * @param {boolean} [options.autoClose=true] - Whether to automatically close the notification
 * @param {number} [options.duration=5000] - Duration in ms before auto-closing
 * @param {Function} [options.onClose] - Callback to execute after notification is closed
 * @param {Object} [options.action] - Optional action button config { label, onClick }
 * @returns {Function} - A function to close the notification
 */
function show(options) {
  createContainer();
  
  const {
    type = 'info',
    title = '',
    message = '',
    dismissible = true,
    autoClose = true,
    duration = DEFAULT_DURATION,
    onClose,
    action,
  } = options;
  
  // Create a wrapper div for this specific notification
  const notificationWrapper = document.createElement('div');
  notificationContainer.appendChild(notificationWrapper);
  
  // Create a Vue app instance for this notification
  const app = createApp({
    render() {
      return h(AlertNotification, {
        type,
        title,
        message,
        dismissible,
        autoClose,
        duration,
        hasAction: !!action,
        onClose: () => close(),
      }, action ? {
        action: () => h('button', {
          class: 'button is-small',
          onClick: () => {
            action.onClick();
            close();
          },
        }, action.label),
      } : undefined);
    },
  });
  
  // Mount the app to the wrapper
  app.mount(notificationWrapper);
  
  // Function to close this specific notification
  function close() {
    setTimeout(() => {
      app.unmount();
      if (notificationWrapper.parentNode) {
        notificationWrapper.parentNode.removeChild(notificationWrapper);
      }
      
      if (onClose && typeof onClose === 'function') {
        onClose();
      }
    }, 300); // Small delay to allow for exit animation
  }
  
  return close;
}

// Convenience methods for each notification type
const success = (options) => show({ ...options, type: 'success', autoClose: true });
const info = (options) => show({ ...options, type: 'info', autoClose: true });
const warning = (options) => show({ ...options, type: 'warning' });
const error = (options) => show({ ...options, type: 'error' });

export default {
  show,
  success,
  info,
  warning,
  error,
};